/* global analytics */
import { useEffect } from 'react'
import styled from 'styled-components'
import { useLocation } from 'react-router'

import useReduxSelector from 'hooks/useReduxSelector'
import { useRestoreSiteFooter } from 'hooks/useSiteFooter'
import { dispatchRollbarError } from 'utils/rollbar'
import { media, mediaMax } from 'styles/media'
import { useUtmParams } from 'utils/utmParams'
import useHideIntercom from 'hooks/useHideIntercom'

import Container from '../../components/layout/Container'
import ScrollTop from '../../components/utils/ScrollTop'
import JsonLd from '../../components/utils/JsonLd'
import { itemListJsonLd } from '../../components/utils/structuredData'

import {
  GlobalFeaturedAdventures,
  MarketFeaturedAdventures,
} from './FeaturedContent/Adventures'
import Header from './Header'
import HostInfo from './HostInfo'
import Intro from './Intro'
import RegionLinks from './EscapeLinks/RegionLinks'
import CountryAndStateLinks from './EscapeLinks/CountryAndStateLinks'
import Stories from './FeaturedContent/Stories'
import VoucherInfo from './VoucherInfo'
import FulfillingMoments from './FulfillingMoments'
import PromoBanner from './PromoBanner'
import {
  GlobalFeaturedCollections,
  MarketFeaturedCollections,
} from './FeaturedContent/Collections'

const SpaceOnTop = styled.div`
  margin: 72px 0 0 0;

  ${media.md`
    margin-top: 120px;
  `}
`

const ResponsiveContainer = styled(Container)`
  ${mediaMax.md`
    padding: 0;
  `}
`

const useAnalyticsPageViewed = (marketCode: GQL_MarketCode) => {
  const { pathname } = useLocation()
  const utmParams = useUtmParams()

  const properties = {
    url: pathname,
    ...(marketCode ? { market: marketCode } : {}),
    ...utmParams,
  }

  useEffect(() => {
    analytics.track('Homepage Viewed', properties)
  }, [])
}

// Set by the reload-recovery effect below to avoid loops when SSR also returns empty data.
const RELOAD_GUARD_KEY = 'home-data-reload-attempted'

// sessionStorage can throw a DOMException when storage is blocked (common in
// embedded contexts like the Facebook in-app browser — i.e. exactly the
// demographic this recovery path targets). A throw inside the effect would
// trip the RollbarErrorBoundary instead of recovering.
const safeSessionStorage = {
  get(key: string): string | null {
    try {
      return window.sessionStorage?.getItem(key) ?? null
    } catch {
      return null
    }
  },
  set(key: string, value: string): boolean {
    try {
      window.sessionStorage?.setItem(key, value)
      return true
    } catch {
      return false
    }
  },
  remove(key: string): void {
    try {
      window.sessionStorage?.removeItem(key)
    } catch {
      // best-effort cleanup; nothing actionable if storage is blocked
    }
  },
}

const usePrerenderedData = () => {
  const reduxState = useReduxSelector((state) => state?.home)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (reduxState?.data != null) return

    // The home reducer is only ever seeded by Rails SSR preloadedState
    // (see `app/views/home/index.html.slim`). If a user lands on a non-home
    // route first (e.g. deep-linking into /stories/<slug> from Facebook) and
    // then navigates client-side to a home market route, the data is missing
    // and the page would crash. Force a full reload of the current URL so
    // Rails re-renders with the home data populated.
    if (safeSessionStorage.get(RELOAD_GUARD_KEY) === '1') {
      dispatchRollbarError(
        'Home page prerendered data still missing after reload',
        {
          description:
            'usePrerenderedData triggered a window.location.replace to re-fetch SSR home data, but the data is still missing after the reload. The Rails home view is not seeding state.home.data as expected.',
          homePageReduxState: reduxState,
          url: window.location.href,
        },
      )
      return
    }

    // If we can't persist the guard (sessionStorage blocked), skip the reload
    // entirely — a reload without the guard would loop on every paint. Report
    // it so we can see how often this path fires for blocked-storage clients.
    if (!safeSessionStorage.set(RELOAD_GUARD_KEY, '1')) {
      dispatchRollbarError(
        'Home page prerendered data missing and sessionStorage unavailable; skipping reload',
        {
          description:
            'Cannot persist the reload guard (sessionStorage blocked or throwing), so a reload would loop. Falling back to a no-op recovery; the page will render with empty data.',
          homePageReduxState: reduxState,
          url: window.location.href,
        },
      )
      return
    }

    window.location.replace(window.location.pathname + window.location.search)
  }, [reduxState])

  useEffect(() => {
    if (reduxState?.data != null) {
      safeSessionStorage.remove(RELOAD_GUARD_KEY)
    }
  }, [reduxState])

  const { data } = reduxState

  const marketCode = data?.currentMarket?.code as GQL_MarketCode
  const country = {
    ...data?.country,
    id: data?.country?.id?.toString(),
  }

  const states = data?.states.map((state) => ({
    ...state,
    id: state?.id?.toString(),
    countryId: state?.country_id?.toString(),
  }))

  const countryCode = country?.code as GQL_Countries

  const stories: GQL_StoryCardFragmentFragment[] = data?.stories?.map(
    (story) =>
      ({
        ...story,
        __typename: 'Story',
        id: story?.id?.toString(),
        regionId: story?.region_id?.toString(),
        user: {
          ...story?.user,
          firstName: story?.user?.first_name?.toString(),
          avatar: {
            original: story?.user?.avatar,
            thumbLg: story?.user?.avatar,
          },
        },
        promoImage: {
          medium: story?.promo_image?.medium,
          large: story?.promo_image?.medium,
        },
        featuredListingsImage: {
          medium: story?.featured_listings_image?.medium,
          large: story?.featured_listings_image?.medium,
        },
        videoUrl: story?.video_url,
        featuredListing: {
          ...story?.featured_listing?.listing,
          pricingSuffix: story?.featured_listing?.listing?.pricing_suffix,
          nicePricingSuffix:
            story?.featured_listing?.listing?.nice_pricing_suffix,
          reviewScore: story?.featured_listing?.listing?.review_score,
          reviewCount: (
            story?.featured_listing?.listing as { review_count?: number }
          )?.review_count,
          instantBookEnabled:
            story?.featured_listing?.listing?.instant_book_enabled,
        },
      }) as unknown as GQL_StoryCardFragmentFragment,
  )

  const marketName = data?.currentMarket?.name

  const primaryCity = data?.currentMarket?.primary_city

  const regions = data?.regions?.map((region) => ({
    ...region,
    id: region?.id?.toString(),
    state: {
      ...region?.state,
      id: region?.state?.id?.toString(),
      countryId: region?.state?.country_id?.toString(),
    },
  }))

  const countriesWithStates =
    data?.countries
      ?.filter((c) => c.discoverable)
      ?.map((c) => ({
        ...c,
        id: c?.id?.toString(),
      })) ?? []

  const fulfillingMomentCount = data?.fulfillingMomentCount

  return {
    country,
    countriesWithStates,
    countryCode,
    marketCode,
    marketName,
    primaryCity,
    regions,
    states,
    stories,
    fulfillingMomentCount,
  }
}

const HomePage = () => {
  const {
    country,
    countriesWithStates,
    countryCode,
    marketCode,
    marketName,
    primaryCity,
    regions,
    states,
    stories,
    fulfillingMomentCount,
  } = usePrerenderedData()

  useAnalyticsPageViewed(marketCode)

  useRestoreSiteFooter()
  useHideIntercom()

  return (
    <>
      <JsonLd data={itemListJsonLd(stories)} />
      <ScrollTop />
      <PromoBanner marketCode={marketCode} />
      <Header
        marketCode={marketCode}
        country={country}
        states={states}
        countries={countriesWithStates}
        primaryCity={primaryCity}
      />
      <ResponsiveContainer>
        <SpaceOnTop>
          <Stories
            marketCode={marketCode}
            countryCode={countryCode}
            stories={stories}
            isEmpty={stories?.length === 0}
            loading={!stories}
          />
        </SpaceOnTop>
      </ResponsiveContainer>

      <Container>
        <SpaceOnTop>
          <Intro />
        </SpaceOnTop>
      </Container>

      <SpaceOnTop>
        {marketCode ? (
          <MarketFeaturedCollections marketCode={marketCode} />
        ) : (
          <GlobalFeaturedCollections countryCode={countryCode} />
        )}
      </SpaceOnTop>

      <SpaceOnTop>
        {marketCode ? (
          <MarketFeaturedAdventures
            marketCode={marketCode}
            countryCode={countryCode}
          />
        ) : (
          <GlobalFeaturedAdventures countryCode={countryCode} />
        )}
      </SpaceOnTop>

      <Container>
        <SpaceOnTop>
          <VoucherInfo />
        </SpaceOnTop>
      </Container>

      <SpaceOnTop>
        <HostInfo />
      </SpaceOnTop>

      <Container>
        <SpaceOnTop>
          <FulfillingMoments count={fulfillingMomentCount} />
        </SpaceOnTop>

        <SpaceOnTop>
          {marketName ? (
            <RegionLinks
              marketName={marketName}
              country={country}
              regions={regions}
            />
          ) : (
            <CountryAndStateLinks countries={countriesWithStates} />
          )}
        </SpaceOnTop>
      </Container>
    </>
  )
}

export default HomePage
